<template>
  <div
    class="season-grid"
    :data-testid="`${testid}-custom-picker-season`"
  >
    <v-list class="season-list-items">
      <v-list-item
        v-for="season in seasons"
        :key="season.value"
        class="season-list-item"
        :class="{
          selected: isSeasonSelected(season),
          disabled: isDisabled(season),
          current: isCurrentSeason(season),
        }"
        :disabled="isDisabled(season)"
        @click="!isDisabled(season) && $emit('select', season)"
      >
        {{ getLabel(season.value) }}
      </v-list-item>
    </v-list>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { getSeasonLabel } from "@/util/datetime";

interface Season {
  key: string
  value: number
}

type DisabledFn = (season: Season) => boolean

export default defineComponent({
  name: 'SeasonGrid',
  props: {
    seasons: {
      type: Array as PropType<Season[]>,
      required: true,
    },
    selected: {
      type: Number,
      default: null,
    },
    locale: {
      type: String,
      required: true
    },
    isDisabled: {
      type: Function as PropType<DisabledFn>,
      required: true,
    },
    viewYear: { type: Number, required: true },
    selectedYear: { type: Number, required: true },
    testid: {
      type: String,
      required: true,
    },
  },
  emits: ['select'],
  setup(props) {
    const isCurrentSeason = (season: { value: number }) => {
      const today = new Date();
      const month = today.getMonth() + 1;
      const currentYear = new Date().getFullYear();
      const currentSeason = (() => {
        if ([12,1,2].includes(month)) return 12;
        if ([3,4,5].includes(month)) return 3;
        if ([6,7,8].includes(month)) return 6;
        return 9;
      })()

      return season.value === currentSeason && props.viewYear === currentYear;
    }

    const isSeasonSelected = (season: Season) => {
      return props.selectedYear === props.viewYear && props.selected === season.value
    }

    const getLabel = (value: number) => getSeasonLabel(value, props.locale);

    return {
      isCurrentSeason,
      isSeasonSelected,
      getLabel,
    }
  }
})
</script>

<style scoped>
.season-grid {
  width: 350px;
  height: 287px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 1rem;
  background-color: rgb(var(--v-theme-background));
}

.season-list-items {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.8rem;
  width: 100%;
  list-style: none;
  padding: 0;
  margin: 0;
}

.season-list-item {
  font-size: 0.875rem;
  cursor: pointer;
  width: 60%;
  margin: 0 auto;
  text-align: center;
  padding: 0.75rem 0;
  transition: background 0.2s, color 0.2s;
  background-color: white;
}

.season-list-item:hover {
  background-color: rgb(var(--v-theme-primary) / 0.1);
}

.season-list-item.selected {
  background-color: rgb(var(--v-theme-primary));
  color: white;
}

.season-list-item.disabled {
  cursor: not-allowed;
  color: rgba(var(--v-theme-on-surface), var(--v-disabled-opacity));
}

.season-list-item.current {
  border: 1px solid rgb(var(--v-theme-primary));
  box-sizing: border-box;
}
</style>